import { useEffect, useState } from 'react';
import { useCatalogSearch, useCreateCondition, useUpdateCondition } from '@/hooks/useMedical';
import type { CatalogEntry, PersonCondition } from '@/api/medical.api';
import {
  CATEGORY_LABELS,
  INHERITANCE_LABELS,
  STATUS_OPTIONS,
  STATUS_LABELS,
  ASCERTAINMENT_OPTIONS,
  ASCERTAINMENT_LABELS,
  CONDITION_CERTAINTY_OPTIONS,
  CONDITION_CERTAINTY_LABELS,
} from './medicalLabels';

interface Props {
  personId: string;
  condition?: PersonCondition;
  onClose: () => void;
}

export function ConditionForm({ personId, condition, onClose }: Props) {
  const isEdit = !!condition;
  const createMut = useCreateCondition(personId);
  const updateMut = useUpdateCondition(personId);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [catalog, setCatalog] = useState<CatalogEntry | null>(condition?.catalog ?? null);
  const [freeText, setFreeText] = useState(condition?.freeText ?? '');
  const [status, setStatus] = useState<string>(condition?.status ?? 'active');
  const [ascertainment, setAscertainment] = useState<string>(condition?.ascertainment ?? 'not_assessed');
  const [certainty, setCertainty] = useState<string>(condition?.certainty ?? 'reported');
  const [ageAtOnset, setAgeAtOnset] = useState(condition?.ageAtOnset != null ? String(condition.ageAtOnset) : '');
  const [diagnosedDateText, setDiagnosedDateText] = useState(condition?.diagnosedDateText ?? '');
  const [isCauseOfDeath, setIsCauseOfDeath] = useState(condition?.isCauseOfDeath ?? false);
  const [notes, setNotes] = useState(condition?.notes ?? '');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results } = useCatalogSearch(debounced.length >= 2 ? { q: debounced } : undefined);
  const showResults = debounced.length >= 2 && !catalog;

  const submitting = createMut.isPending || updateMut.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dto = {
      catalog_id: catalog?.id ?? null,
      free_text: catalog ? null : freeText.trim() || null,
      status,
      ascertainment,
      certainty,
      age_at_onset: ageAtOnset ? Number(ageAtOnset) : null,
      diagnosed_date_text: diagnosedDateText.trim() || null,
      is_cause_of_death: isCauseOfDeath,
      notes: notes.trim() || null,
    };
    const opts = { onSuccess: onClose };
    if (isEdit && condition) updateMut.mutate({ id: condition.id, dto }, opts);
    else createMut.mutate(dto, opts);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Diagnose bearbeiten' : 'Diagnose hinzufügen'}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Schließen">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {/* Katalog-Auswahl */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Diagnose (Katalog)</label>
            {catalog ? (
              <div className="flex items-center justify-between rounded border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-sm">
                <span>
                  <span className="font-medium text-slate-800">{catalog.label}</span>
                  <span className="ml-2 text-xs text-slate-500">
                    {CATEGORY_LABELS[catalog.category]}
                    {catalog.isHereditary && catalog.inheritance ? ` · ${INHERITANCE_LABELS[catalog.inheritance] ?? catalog.inheritance}` : ''}
                  </span>
                </span>
                <button type="button" onClick={() => setCatalog(null)} className="text-xs text-blue-600 hover:underline">
                  ändern
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Katalog durchsuchen (z.B. Tuberkulose, Diabetes)…"
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                {showResults && (
                  <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-lg">
                    {(results ?? []).length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400">Keine Treffer – nutze unten den Freitext.</div>
                    ) : (
                      (results ?? []).map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => { setCatalog(r); setQuery(''); }}
                          className="flex w-full flex-col items-start px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                        >
                          <span className="font-medium text-slate-800">{r.label}</span>
                          <span className="text-xs text-slate-500">
                            {CATEGORY_LABELS[r.category]}
                            {r.icd10 ? ` · ICD-10 ${r.icd10}` : ''}
                            {r.isHereditary ? ' · erblich' : ''}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Freitext (wenn kein Katalog) */}
          {!catalog && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">…oder Freitext</label>
              <input
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="z.B. Schwindsucht"
                className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Select label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} labels={STATUS_LABELS} />
            <Select label="Beleglage" value={certainty} onChange={setCertainty} options={CONDITION_CERTAINTY_OPTIONS} labels={CONDITION_CERTAINTY_LABELS} />
          </div>

          <Select
            label="Erfassungsgrad"
            value={ascertainment}
            onChange={setAscertainment}
            options={ASCERTAINMENT_OPTIONS}
            labels={ASCERTAINMENT_LABELS}
            hint='"nicht untersucht" ≠ "kein Befund" – wichtig für Statistik & Vererbung.'
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Diagnose-Datum</label>
              <input
                value={diagnosedDateText}
                onChange={(e) => setDiagnosedDateText(e.target.value)}
                placeholder="z.B. 1972"
                className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Alter bei Beginn</label>
              <input
                type="number"
                min={0}
                max={130}
                value={ageAtOnset}
                onChange={(e) => setAgeAtOnset(e.target.value)}
                className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={isCauseOfDeath} onChange={(e) => setIsCauseOfDeath(e.target.checked)} className="rounded border-slate-300" />
            Als Todesursache markieren
          </label>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Notizen</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting || (!catalog && !freeText.trim())}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Select<T extends string>({
  label, value, onChange, options, labels, hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly T[];
  labels: Record<string, string>;
  hint?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
      >
        {options.map((o) => (
          <option key={o} value={o}>{labels[o] ?? o}</option>
        ))}
      </select>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}
